/*
 * Copyright (C) 2018 Spanning Cloud Apps. LLC  All rights reserved.
 */
import { ValidationUtils } from 'spanalog/lib/utils/ValidationUtils';

/*
 * This module maintains the state of the wizard and provides functions for moving through the steps.
 */
factory.$inject = ['$q', 'UtilService', 'WizardDirection', 'WizardPageFactory'];

/**
 * Defines the factory that creates the WizardService object used to communicate state between the pages in the
 * wizard.
 *
 * @returns {object} the WizardService object. The caller should modify the object to provide the desired behavior.
 */
export function factory($q, UtilService, WizardDirection, WizardPageFactory) {
  /**
   * The name or a function returning the name of the initial angular-ui state of the wizard. The initial state
   * determines the available steps.
   *
   * @type {string | function():string}
   */
  var initialState;

  /**
   * The name or a function returning the name of the current angular-ui state of the wizard. The current state updates
   * as we step through the wizard
   *
   * @type {string | function():string}
   */
  var currentState;

  /**
   * The name or a function returning the name of the state to go to when the Next button is pressed.
   *
   * @type {string | function():string}
   */
  var nextState;

  /**
   * The common prefix or a function returning the common prefix of all the angular-ui states in the wizard. This is
   * used to correctly handle state changes away from the wizard.
   *
   * @type {string | function():string}
   */
  var statePrefix;

  /**
   * angular-ui states the user has previously visited. This gets populated as the user hits Next, and depopulated as
   * the user hits Prev.
   *
   * @type {string[]}
   */
  var stateHistory = [];

  /**
   * An array or a function returning an array of the steps in the process defined using the i18n message keys.
   * null in the last position means we don't yet know the remaining steps.
   *
   * @type {string[] | function():string[]}
   */
  var stepKeys = [];

  /**
   * The 0-based index of the current step.
   */
  var currentStep = 0;

  /**
   * A boolean or a function returning a boolean that indicates whether the user has interacted with the wizard. This is
   * used for determining whether a confirmation should be displayed when the user clicks away from the wizard.
   * Defaults to true.
   *
   * @type {boolean | function():boolean}
   */
  var touched;

  /**
   * The I18N message key or a function returning the I18N message key for the text to be displayed when confirming
   * cancellation of the wizard.
   *
   * @type {string | function():string}
   */
  var cancelConfirmTextKey;

  /**
   * Page-specific settings. Pages are indexed by the 0-based step number.
   *
   * @type {WizardPage[]} - an array of WizardPage objects.
   */
  var pages = [];

  // Expose the functions only, not the data
  return {
    getCancelConfirmTextKey: getCancelConfirmTextKey,
    getCurrentPage: getCurrentPage,
    getCurrentState: getCurrentState,
    getCurrentStep: getCurrentStep,
    getInitialState: getInitialState,
    getNextState: getNextState,
    getStatePrefix: getStatePrefix,
    getStepKeys: getStepKeys,
    getTouched: getTouched,
    goToStep: goToStep,
    isCompletedStep: isCompletedStep,
    isCurrentStep: isCurrentStep,
    isFirstStep: isFirstStep,
    isLastStep: isLastStep,
    isNextEnabled: isNextEnabled,
    isNextVisible: isNextVisible,
    isPrevEnabled: isPrevEnabled,
    isUnknownStep: isUnknownStep,
    next: next,
    prev: prev,
    reset: reset,
    setCancelConfirmTextKey: setCancelConfirmTextKey,
    setCurrentState: setCurrentState,
    setInitialState: setInitialState,
    setNextState: setNextState,
    setStatePrefix: setStatePrefix,
    setStepKeys: setStepKeys,
    setTouched: setTouched
  };

  /**
   * Returns the WizardPage object for the current step. A new page is created if necessary.
   *
   * @return {WizardPage} the WizardPage object for the current step.
   */
  function getCurrentPage() {
    var page = pages[currentStep];

    if (!page) {
      page = pages[currentStep] = WizardPageFactory.generate();
    }

    return page;
  }

  /**
   * Returns the I18N message key for the text to be displayed when confirming cancellation of the wizard.
   *
   * @returns {string} the I18N message key for the text to be displayed when confirming cancellation of the wizard.
   */
  function getCancelConfirmTextKey() {
    return UtilService.getString(cancelConfirmTextKey) || 'CONFIRM_WIZARD_EXIT';
  }

  /**
   * Sets the I18N message key for the text to be displayed when confirming cancellation of the wizard.
   *
   * @param {string | function():string} textOrFn - The I18N message key or a function returning I18N message key for
   * the text to be displayed when confirming cancellation of the wizard.
   */
  function setCancelConfirmTextKey(textOrFn) {
    if (UtilService.isSet(textOrFn)) {
      ValidationUtils.assertIsStringOrFn(textOrFn, 'cancelConfirmTextKey');
      cancelConfirmTextKey = textOrFn;
    } else {
      cancelConfirmTextKey = undefined;
    }
  }

  /**
   * Returns the name of the current angular-ui state of the wizard.
   *
   * @returns {string} the name of the current angular-ui state of the wizard.
   */
  function getCurrentState() {
    return UtilService.getString(currentState);
  }

  /**
   * Sets the name of the current angular-ui state of the wizard.
   *
   * @param {string | function():string} stateOrFn - The name or a function returning the name of the current angular-ui
   * state of the wizard.
   */
  function setCurrentState(stateOrFn) {
    if (UtilService.isSet(stateOrFn)) {
      ValidationUtils.assertIsStringOrFn(stateOrFn, 'currentState');
      currentState = stateOrFn;
    } else {
      currentState = undefined;
    }
  }

  /**
   * Returns the current step index.
   *
   * @returns {number} the 0-based step index of the current page.
   */
  function getCurrentStep() {
    return currentStep;
  }

  /**
   * Returns the name of the initial angular-ui state of the wizard.
   *
   * @returns {string} the name of the initial angular-ui state of the wizard.
   */
  function getInitialState() {
    return UtilService.getString(initialState);
  }

  /**
   * Sets the name of the initial angular-ui state of the wizard.
   *
   * @param {string | function():string} stateOrFn - The name or a function returning the name of the initial angular-ui
   * state of the wizard.
   */
  function setInitialState(stateOrFn) {
    if (UtilService.isSet(stateOrFn)) {
      ValidationUtils.assertIsStringOrFn(stateOrFn, 'initialState');
      initialState = stateOrFn;

      // Setting the initial state also sets the current state if it's not already defined.
      if (!UtilService.isSet(currentState)) {
        currentState = initialState;
      }
    } else {
      initialState = undefined;
    }
  }

  /**
   * Returns the name of the next angular-ui state of the wizard.
   *
   * @returns {string} the name of the next angular-ui state of the wizard.
   */
  function getNextState() {
    return UtilService.getString(nextState);
  }

  /**
   * Sets the name of the next angular-ui state of the wizard.
   *
   * @param {string | function():string} stateOrFn - The name or a function returning the name of the next angular-ui
   * state of the wizard.
   */
  function setNextState(stateOrFn) {
    if (UtilService.isSet(stateOrFn)) {
      ValidationUtils.assertIsStringOrFn(stateOrFn, 'nextState');
      nextState = stateOrFn;
    } else {
      nextState = undefined;
    }
  }

  /**
   * Returns the common prefix of all the angular-ui states in the wizard.
   *
   * @returns {string} the common prefix of all the angular-ui states in the wizard.
   */
  function getStatePrefix() {
    return UtilService.getString(statePrefix);
  }

  /**
   * Sets the common prefix of all the angular-ui states in the wizard.
   *
   * @param {string | function():string} prefixOrFn - The prefix or a function returning the prefix of all the
   * angular-ui states in the wizard.
   */
  function setStatePrefix(prefixOrFn) {
    if (UtilService.isSet(prefixOrFn)) {
      ValidationUtils.assertIsStringOrFn(prefixOrFn, 'statePrefix');
      statePrefix = prefixOrFn;
    } else {
      statePrefix = undefined;
    }
  }

  /**
   * Returns the array of the steps in the process defined using the i18n message keys.
   *
   * @returns {string[]} the array of the steps in the process defined using the i18n message keys.
   */
  function getStepKeys() {
    return UtilService.getArray(stepKeys);
  }

  /**
   * Sets the array or a function returning an array of the steps in the process defined using the i18n message keys.
   * null in the last position means we don't yet know the remaining steps.
   *
   * @param {string[] | function():string[]} keysOrFn - The array or a function returning an array of the steps in the
   * process defined using the i18n message keys.
   */
  function setStepKeys(keysOrFn) {
    if (UtilService.isSet(keysOrFn)) {
      ValidationUtils.assertIsArrayOrFn(keysOrFn, 'stepKeys');
      stepKeys = keysOrFn;
    } else {
      stepKeys = [];
    }
  }

  /**
   * Determines whether the user has interacted with the wizard.
   *
   * @returns {boolean} true or false, indicating whether the user has interacted with the wizard.
   */
  function getTouched() {
    // Check whether the wizard or the current page considers itself touched
    return UtilService.getBoolean(touched) || getCurrentPage().getTouched();
  }

  /**
   * Sets the value that indicates whether the user has interacted with the wizard.
   *
   * @param {boolean | function():boolean} booleanOrFn - The value or a function returning the value that indicates
   * whether the user has interacted with the wizard.
   */
  function setTouched(booleanOrFn) {
    if (UtilService.isSet(booleanOrFn)) {
      ValidationUtils.assertIsBooleanOrFn(booleanOrFn, 'touched');
      touched = booleanOrFn;
    } else {
      touched = undefined;
    }
  }

  /**
   * Resets the wizard to the default state.
   */
  function reset() {
    initialState = undefined;
    currentState = undefined;
    nextState = undefined;
    statePrefix = undefined;
    stateHistory = [];
    stepKeys = [];
    currentStep = 0;
    touched = undefined;
    cancelConfirmTextKey = undefined;
    pages = [];
  }

  /**
   * Determines whether a step is a completed step.
   *
   * @param {number} step - The step index to be checked.
   * @returns {boolean} true if the supplied step is a completed step, false otherwise.
   */
  function isCompletedStep(step) {
    return currentStep > step;
  }

  /**
   * Determines whether a step is the current step.
   *
   * @param {number} step - The step index to be checked.
   * @returns {boolean} true if the supplied step is the current step, false otherwise.
   */
  function isCurrentStep(step) {
    return currentStep === step;
  }

  /**
   * Determines whether a step represents the first step.
   *
   * @param {number} step - The step index to be checked.
   * @returns {boolean} true if the supplied step is the first step, false otherwise.
   */
  function isFirstStep(step) {
    return step === 0;
  }

  /**
   * Determines whether a step represents the last step.
   *
   * @param {number} step - The step index to be checked.
   * @returns {boolean} true if the supplied step is the last step, false otherwise.
   */
  function isLastStep(step) {
    return step === stepKeys.length - 1;
  }

  /**
   * Determines whether a step represents that the remaining steps are not yet known.
   *
   * @param {number} step - The step index to be checked.
   * @returns {boolean} true if the supplied step represents and unknown step, false otherwise.
   */
  function isUnknownStep(step) {
    var curKey = stepKeys[step];
    return curKey === null;
  }

  /**
   * Determines whether the next button should be enabled.
   *
   * @returns {boolean} true if the next button should be enabled, false otherwise.
   */
  function isNextEnabled() {
    return getCurrentPage().getValid() || false;
  }

  /**
   * Determines whether the next button should be visible.
   *
   * @returns {boolean} true if the next button should be visible, false otherwise.
   */
  function isNextVisible() {
    return currentStep < stepKeys.length - 1;
  }

  /**
   * Determines whether the prev button should be enabled.
   *
   * @returns {boolean} true if the prev button should be enabled, false otherwise.
   */
  function isPrevEnabled() {
    return stateHistory.length > 0;
  }

  /**
   * Advances to the next page.
   *
   * @returns {Promise} a promise that is resolved when there is nothing to confirm or when the user confirms. The
   * promise is rejected if the user decides to stay.
   */
  function next() {
    // Don't allow advancing past the last page
    if (isLastStep(currentStep)) {
      throw new Error('Already at last step');
    }

    return confirmLeavePage(WizardDirection.FORWARD).then(function () {
      // Save the current state so we can go back to it using prev
      stateHistory.push(currentState);

      // Ask the parent controller what the next state should be
      currentState = getNextState();

      // Advance to the next step
      currentStep++;
    });
  }

  /**
   * Returns to the previous page, optionally confirming leaving the current page.
   *
   * @returns {Promise} a promise that is resolved when there is nothing to confirm or when the user confirms. The
   * promise is rejected if the user decides to stay.
   */
  function prev() {
    // Don't allow retreating past the first page
    if (isFirstStep(currentStep)) {
      throw new Error('Already at first step');
    }

    return confirmLeavePage(WizardDirection.REVERSE).then(prevWithoutConfirm);
  }

  /**
   * Returns to the previous page with no confirmation.
   */
  function prevWithoutConfirm() {
    // Pull the previous state from the history
    currentState = stateHistory.pop();

    // Clear the page settings for the current step. If the user takes a different path forward we don't want the
    // old settings there.
    pages[currentStep] = undefined;

    // Return to the previous step
    currentStep--;
  }

  /**
   * Moves to a specific page in the history
   *
   * @param {number} step - The index of the page to go back to.
   *
   * @returns {Promise} a promise that is resolved when there is nothing to confirm or when the user confirms. The
   * promise is rejected if the user decides to stay.
   */
  function goToStep(step) {
    ValidationUtils.assertAtLeast(step, 0, 'step');

    // Don't allow going forward
    if (step > currentStep) {
      throw new Error('Cannot go to a future step');
    }

    if (step === currentStep) {
      return $q.when();
    }

    return confirmLeavePage(WizardDirection.REVERSE).then(function () {
      // Back up until we get to the desired step
      while (currentStep > step) {
        prevWithoutConfirm();
      }
    });
  }

  /**
   * Gives the current page an opportunity to confirm any possible loss of unsaved changes.
   *
   * @param {String} direction - the WizardDirection the user is moving when they leave.
   * @returns {Promise} a promise that is resolved when there is nothing to confirm or when the user confirms. The
   * promise is rejected if the user decides to stay.
   */
  function confirmLeavePage(direction) {
    return getCurrentPage().confirmLeave(direction);
  }
}
